import { MarketPlaceType } from 'Modules'
import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react'
import { useParams } from 'react-router'

import { AppAdmin } from 'api/app-admin'
import { logError } from 'utils/logger'

import { MarketPlaceProvider } from './market-place-provider'

interface MarketPlaceDetailContextType {
  id?: string | null
  loading?: boolean
  marketPlaceDetail?: MarketPlaceType | null
  getMarketPlaceDetail: () => void
}

const MarketPlaceDetailContext = createContext<MarketPlaceDetailContextType>({
  id: null,
  loading: false,
  marketPlaceDetail: null,
  getMarketPlaceDetail: () => {},
})

interface MarketPlaceDetailContextProps {
  children: ReactNode
}

const MarketPlaceDetailProvider: React.FC<MarketPlaceDetailContextProps> = ({ children }) => {
  const locationParam = useParams()

  const id = locationParam.code || ''

  const [loading, setLoading] = useState(false)

  const [marketPlaceDetail, setMarketPlaceDetail] = useState<any>(null)

  const getMarketPlaceDetail = useCallback(async () => {
    setLoading(true)
    try {
      const res = await AppAdmin.getDetailMarketplace(id)
      if (res?.data.success) {
        const details = res?.data?.data || null
        setMarketPlaceDetail(details)
      }
    } catch (error) {
      logError('Error fetching data: ', error)
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    if (id) {
      getMarketPlaceDetail()
    }
  }, [getMarketPlaceDetail, id])

  return (
    <MarketPlaceDetailContext.Provider
      value={{
        id,
        loading,
        marketPlaceDetail,
        getMarketPlaceDetail,
      }}
    >
      <MarketPlaceProvider>{children}</MarketPlaceProvider>
    </MarketPlaceDetailContext.Provider>
  )
}

const useMarketPlaceDetailContext = (): MarketPlaceDetailContextType => {
  const context = useContext(MarketPlaceDetailContext)

  if (context === undefined) {
    throw new Error('Context not available')
  }

  return context
}

export { MarketPlaceDetailProvider, useMarketPlaceDetailContext }
